import React, { useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Rationale } from './StageTraces';
import SeverityChip from './SeverityChip';
import { Alternatives, ConfidencePill, PolicyRefs } from './DecisionDetails';
import { CustomerRollup, RollupObservation, describeSegments, severityRank, worstSeverity } from './explainabilityTypes';
import { dsVar, ink } from '../../theme/tokens';

/**
 * A customer stage rolled up across the customers it ran for: its decisions by
 * option, with sample decisions, and its observations grouped by type, most
 * severe first. Styled with sx: it renders inside a portalled drawer.
 */

/** Observations at or above this rank are shown; the rest wait behind a toggle. */
const NOTABLE_RANK = severityRank('signal');

const card = { border: `1px solid ${dsVar('line')}`, borderRadius: '10px', backgroundColor: dsVar('page'), p: 1.5 };

const subheading = { fontSize: '0.75rem', fontWeight: 600, color: dsVar('fg-soft'), mb: 0.75 };

const Description: React.FC<{ text: string }> = ({ text }) => {
    const { metrics, flags } = describeSegments(text);
    return (
        <Box sx={{ mt: 0.75 }}>
            {flags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                    {flags.map((flag) => (
                        <Box
                            key={flag}
                            component="span"
                            sx={{ fontSize: '0.625rem', fontWeight: 700, color: ink('#FCD34D'), backgroundColor: 'rgba(251, 191, 36, 0.1)', borderRadius: '4px', px: 0.75, py: 0.25 }}
                        >
                            {flag}
                        </Box>
                    ))}
                </Box>
            )}
            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), lineHeight: 1.6 }}>
                {metrics.join(' · ')}
            </Typography>
        </Box>
    );
};

const ObservationRow: React.FC<{ observation: RollupObservation }> = ({ observation }) => {
    const samples = observation.sample_descriptions ?? [];
    const severities = Object.entries(observation.by_severity ?? {})
        .sort(([a], [b]) => severityRank(b) - severityRank(a));
    return (
        <Box sx={card}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg'), minWidth: 0 }}>
                    {observation.observation_type}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {severities.map(([severity, n]) => (
                        <SeverityChip key={severity} severity={severity} count={observation.count > 1 ? n : undefined} />
                    ))}
                </Box>
            </Box>
            {samples.map((text, i) => <Description key={i} text={text} />)}
            {observation.count > samples.length && samples.length > 0 && (
                <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted'), mt: 0.5 }}>
                    Showing {samples.length} of {observation.count}
                </Typography>
            )}
        </Box>
    );
};

const CustomerRollupSection: React.FC<{ rollup: CustomerRollup }> = ({ rollup }) => {
    const [showQuiet, setShowQuiet] = useState(false);

    const observations = [...rollup.observations].sort((a, b) =>
        severityRank(worstSeverity(b.by_severity) ?? undefined) - severityRank(worstSeverity(a.by_severity) ?? undefined)
        || b.count - a.count);
    const notable = observations.filter((obs) => severityRank(worstSeverity(obs.by_severity) ?? undefined) >= NOTABLE_RANK);
    const quiet = observations.filter((obs) => !notable.includes(obs));
    // With nothing notable, the quiet ones are all there is, so they aren't hidden.
    const quietHidden = notable.length > 0 && !showQuiet;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rollup.decisions.length > 0 && (
                <Box>
                    <Typography sx={subheading}>Decisions</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {rollup.decisions.map((decision) => (
                            <Box key={decision.decision_type} sx={card}>
                                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: dsVar('fg') }}>
                                    {decision.decision_type}
                                    <Box component="span" sx={{ fontWeight: 400, color: dsVar('fg-muted') }}> · {decision.count}</Box>
                                </Typography>
                                {decision.by_option && Object.keys(decision.by_option).length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                                        {Object.entries(decision.by_option).sort(([, a], [, b]) => b - a).map(([option, n]) => (
                                            <Box key={option} component="span" sx={{ fontSize: '0.6875rem', color: dsVar('fg-body'), border: `1px solid ${dsVar('line')}`, borderRadius: '6px', px: 0.75, py: 0.25 }}>
                                                {option} <Box component="span" sx={{ color: dsVar('fg-muted') }}>×{n}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                                {(decision.sample ?? []).map((sample, i) => (
                                    <Box key={sample.decision_id ?? i} sx={{ mt: 1.25, pl: 1.25, borderLeft: '2px solid rgba(52, 211, 153, 0.35)' }}>
                                        <Typography component="div" sx={{ fontSize: '0.75rem', color: dsVar('fg-soft'), mb: 0.25 }}>
                                            {sample.customer_id && <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: dsVar('fg-2') }}>{sample.customer_id} </Box>}
                                            {sample.chosen_option && <>chose <Box component="span" sx={{ color: dsVar('fg'), fontWeight: 600 }}>{sample.chosen_option}</Box></>}
                                            {' '}<ConfidencePill score={sample.confidence_score} />
                                        </Typography>
                                        {sample.rationale_text && <Rationale text={sample.rationale_text} />}
                                        <PolicyRefs codes={sample.policy_refs} />
                                        <Alternatives decision={sample} />
                                    </Box>
                                ))}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {observations.length > 0 && (
                <Box>
                    <Typography sx={subheading}>Observations</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {notable.map((obs) => <ObservationRow key={obs.observation_type} observation={obs} />)}
                        {quiet.length > 0 && notable.length > 0 && (
                            <ButtonBase
                                onClick={() => setShowQuiet((v) => !v)}
                                aria-expanded={showQuiet}
                                sx={{ justifyContent: 'flex-start', gap: 0.5, fontSize: '0.75rem', color: dsVar('fg-2'), py: 0.5 }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: 16, transition: 'transform 150ms', transform: showQuiet ? 'none' : 'rotate(-90deg)' }} />
                                {showQuiet ? 'Hide' : 'Show'} {quiet.length} more with nothing flagged
                            </ButtonBase>
                        )}
                        {!quietHidden && quiet.map((obs) => <ObservationRow key={obs.observation_type} observation={obs} />)}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default CustomerRollupSection;
